/**
 * Release-state cache sync (single source of truth).
 *
 * Every mutation that changes release/approval state, post or variation content,
 * or scheduling MUST call invalidateReleaseState() (directly, or via the
 * useInvalidateRelease() hook) so Creator, Variations, Calendar, Release Board
 * (+ drawer), Dashboard and Brand Portfolio never show stale state. The SSE
 * approval/post handlers call it too, so other users' actions refresh this tab.
 *
 * Strategy: prefix invalidation. Active queries refetch, inactive ones are only
 * marked stale (refetched on next mount), so there is no refetch storm.
 * Never add ad-hoc partial invalidations in mutations for these surfaces; extend
 * this helper instead.
 *
 * Release sections (dashboard rows, nav badges, calendar nudge, Portfolio inbox) are covered
 * by the prefixes below, so they need no extra call:
 * - teamQueryKeys.dashboard.releaseSections  -> dashboard.all
 * - teamQueryKeys.approval.board.badges / .newlyReleased -> approval.board.all (or approval.all)
 * - releaseInboxKeys (['agency','inbox','release',...]) -> ['agency','inbox']
 * Marking a surface as seen (PUT .../surfaces/:surface/seen) must NOT call this helper: it only
 * moves the viewer's stamp forward; the nav badge cache is patched directly instead.
 * The 'comment' scope leaves the board and sections alone on purpose: sections read the release
 * record's own comment, not chat comments.
 */

import { useCallback } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { teamQueryKeys } from '@/lib/queryKeys';
import { usePostsStore } from '@/stores/postsStore';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { broadcastReleaseState } from '@/lib/releaseSync';

export interface InvalidateReleaseOptions {
  /** Effective team (currentTeamId in God Mode). */
  teamId: number | null;
  /**
   * Additional teams whose keys must be invalidated too (e.g. the brand a viewer
   * acted on from the Brand Portfolio, or the team an SSE event belongs to).
   * Duplicates of teamId are ignored.
   */
  alsoTeamIds?: ReadonlyArray<number | null | undefined>;
  /** When known, approval detail queries are invalidated for this post only. */
  postId?: number;
  /** Informational: variation detail queries are invalidated by prefix anyway. */
  variationId?: number;
  /**
   * 'full' (default): release/post/variation/scheduling state changed.
   * 'comment': chat-only change (status-neutral); invalidates just approval keys
   * for the post, notifications and agency inbox. The Release Board (columns, counts) is NOT
   * invalidated: its cards carry no comment data, so a comment cannot change what they show.
   */
  scope?: 'full' | 'comment';
  /**
   * Default true: also tell the user's other tabs (BroadcastChannel). Pass false when the
   * invalidation itself came from another tab or from SSE (already reaches every tab).
   */
  broadcast?: boolean;
}

function invalidateCommentScope(
  queryClient: QueryClient,
  teamId: number | null,
  postId: number | undefined
): void {
  const invalidate = (queryKey: readonly unknown[]): void => {
    void queryClient.invalidateQueries({ queryKey });
  };
  const approvalAll = teamQueryKeys.approval.all(teamId);
  if (postId !== undefined) {
    invalidate(teamQueryKeys.approval.post(teamId, postId));
    invalidate(teamQueryKeys.approval.history(teamId, postId));
    // Prefix: covers every version thread of the post (Original + each variation)
    invalidate(teamQueryKeys.approval.comments(teamId, postId));
    invalidate([...approvalAll, 'release-detail', postId]);
    invalidate([...approvalAll, 'review-diff', postId]);
  } else {
    invalidate(approvalAll);
  }
  invalidate(teamQueryKeys.notifications.all(teamId));
  invalidate(['agency', 'inbox']);
}

function invalidateFullScope(
  queryClient: QueryClient,
  teamId: number | null,
  postId: number | undefined
): void {
  const invalidate = (queryKey: readonly unknown[]): void => {
    void queryClient.invalidateQueries({ queryKey });
  };

  // Post lists + details + stats (Creator RQ consumers, Calendar sidebar, pickers)
  invalidate(teamQueryKeys.posts.all(teamId));
  // Calendar grid (monthly / multi-calendar)
  invalidate(teamQueryKeys.calendarPosts.all(teamId));

  // Release Board (paged columns + counts), drawer (release-detail), comments, history, per-post approvals.
  // Prefix by post when known; the board is always broad.
  const approvalAll = teamQueryKeys.approval.all(teamId);
  if (postId !== undefined) {
    invalidate(teamQueryKeys.approval.board.all(teamId));
    invalidate(teamQueryKeys.approval.post(teamId, postId));
    invalidate(teamQueryKeys.approval.history(teamId, postId));
    // Prefix: covers every version thread of the post (Original + each variation)
    invalidate(teamQueryKeys.approval.comments(teamId, postId));
    invalidate([...approvalAll, 'release-detail', postId]);
    invalidate([...approvalAll, 'review-diff', postId]);
  } else {
    invalidate(approvalAll);
  }

  // Dashboard sections (stats, pipeline counts, goals progress)
  invalidate(teamQueryKeys.dashboard.all(teamId));
  // Bell + unread count (approval actions create notifications)
  invalidate(teamQueryKeys.notifications.all(teamId));
}

export function invalidateReleaseState(
  queryClient: QueryClient,
  { teamId, alsoTeamIds, postId, scope = 'full', broadcast = true }: InvalidateReleaseOptions
): void {
  const invalidate = (queryKey: readonly unknown[]): void => {
    void queryClient.invalidateQueries({ queryKey });
  };

  // Unique, valid team set: viewer's effective team + any acted-on / event teams
  const teams: Array<number | null> = [teamId];
  for (const id of alsoTeamIds ?? []) {
    if (typeof id === 'number' && Number.isFinite(id) && !teams.includes(id)) teams.push(id);
  }

  if (broadcast) broadcastReleaseState({ teamIds: teams, postId, scope });

  for (const id of teams) {
    if (scope === 'comment') invalidateCommentScope(queryClient, id, postId);
    else invalidateFullScope(queryClient, id, postId);
  }

  if (scope === 'comment') return;

  // Variations (not yet team-scoped keys, see regression catalog)
  invalidate(['variations']);
  invalidate(['variation']);
  invalidate(['team-variations']);
  invalidate(['variation-titles']);
  // Variation creator parent-post picker (not team-scoped)
  invalidate(['posts', 'for-variation']);

  // Dashboard trend suggestions (['dashboard-trends', teamId], not under dashboard.all)
  invalidate(['dashboard-trends']);

  // Scheduling surfaces
  invalidate(['calendar-mappings']);
  invalidate(['calendar-mapping']);
  invalidate(['scheduled-instances']);

  // Brand Portfolio pipeline / workload / highlights and the release inbox
  // (agency-scoped, not team-keyed)
  invalidate(['agency', 'portfolio']);
  invalidate(['agency', 'inbox']);
  invalidate(['agency', 'workspace']);

  // Creator list lives in a Zustand store, not React Query: bridge it
  // (silent: keeps loaded pages, no loading flash).
  void usePostsStore.getState().refreshIfActive();
}

/** Hook form: binds queryClient + effective team. Stable across renders. */
export function useInvalidateRelease(): (
  opts?: Omit<InvalidateReleaseOptions, 'teamId'>
) => void {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();
  return useCallback(
    (opts) => invalidateReleaseState(queryClient, { teamId, ...opts }),
    [queryClient, teamId]
  );
}
